import React, { useState, useRef, useCallback } from 'react';
import { cn } from '../../lib/utils';
import { IconPlus } from '../shared/icons';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getTileColor } from '../../constants/slideTileColors';

/**
 * Slide tile — compact instrument-style button with colored LED.
 */
function SortableSlideCard({ id, index, color, isActive, isPendingDelete, canDelete, onSelect, onRequestDelete, onDuplicate }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    '--tile-color': color.hex,
    '--tile-color-rgb': color.rgb,
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    onRequestDelete(index, e);
  };

  const handleDuplicate = (e) => {
    e.stopPropagation();
    if (onDuplicate) onDuplicate(index);
  };

  const label = String(index + 1).padStart(2, '0');

  return (
    <button
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={() => onSelect(index)}
      className={cn(
        'group slide-card',
        isActive ? 'slide-card-active' : 'slide-card-inactive',
        isDragging && 'slide-card-dragging',
        isPendingDelete && 'slide-card-pending-delete'
      )}
      aria-label={`Slide ${index + 1}`}
    >
      <span className="slide-led" aria-hidden="true" />
      <span className="slide-number">{label}</span>

      {canDelete && !isPendingDelete && (
        <span
          onClick={handleDelete}
          className="corner-badge corner-badge-tl corner-badge-sm corner-badge-delete corner-badge-hover"
          title="Remove slide"
          role="button"
          aria-label={`Remove slide ${index + 1}`}
        >
          <svg width="7" height="7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round">
            <path d="M6 18L18 6M6 6l12 12" />
          </svg>
        </span>
      )}

      {!isPendingDelete && (
        <span
          onClick={handleDuplicate}
          className="corner-badge corner-badge-tr corner-badge-sm corner-badge-duplicate corner-badge-hover"
          title="Duplicate slide"
          role="button"
          aria-label={`Duplicate slide ${index + 1}`}
        >
          <svg width="7" height="7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
            <rect x="9" y="9" width="13" height="13" rx="2" />
            <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
          </svg>
        </span>
      )}

      <span className="slide-bar" aria-hidden="true" />
    </button>
  );
}

/**
 * Horizontal slide tile list with drag-and-drop reordering,
 * instrument-style tiles, inline delete confirmation popup,
 * and colored LED indicators.
 */
export function SlideList({ slides, activeIndex, onSelect, onReorder, onAdd, onRemove, onDuplicate }) {
  const list = Array.isArray(slides) ? slides : [];
  const items = list.map((s, i) => (s && s.id) ? s.id : `slide-pos-${i}`);
  const canDelete = list.length > 1;

  const [pendingDeleteIndex, setPendingDeleteIndex] = useState(null);
  const [popupPos, setPopupPos] = useState({ top: 0, left: 0 });
  const containerRef = useRef(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.indexOf(active.id);
    const newIndex = items.indexOf(over.id);
    if (oldIndex !== -1 && newIndex !== -1 && onReorder) {
      onReorder(oldIndex, newIndex);
    }
  };

  const handleRequestDelete = useCallback((index, e) => {
    if (list.length <= 1) return;

    const badge = e.currentTarget;
    const btn = badge.closest('.slide-card');
    const container = containerRef.current;

    if (btn && container) {
      const containerRect = container.getBoundingClientRect();
      const btnRect = btn.getBoundingClientRect();
      const popupW = 240;

      let left = (btnRect.left + btnRect.width / 2) - containerRect.left - popupW / 2;
      const maxLeft = containerRect.width - popupW;
      left = Math.max(0, Math.min(left, maxLeft));

      setPopupPos({
        top: btnRect.bottom - containerRect.top + 4,
        left,
      });
    }

    setPendingDeleteIndex(index);
  }, [list.length]);

  const handleDeleteConfirm = () => {
    if (pendingDeleteIndex !== null) {
      onRemove(pendingDeleteIndex);
    }
    setPendingDeleteIndex(null);
  };

  const handleDeleteCancel = (e) => {
    if (e) e.stopPropagation();
    setPendingDeleteIndex(null);
  };

  const activeColor = list[activeIndex] ? getTileColor(list[activeIndex], activeIndex) : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={items} strategy={horizontalListSortingStrategy}>
        <div
          className="text-xs uppercase tracking-wider mt-3 mb-1.5 transition-colors duration-200"
          style={{ color: activeColor ? `rgba(${activeColor.rgb}, 0.5)` : 'rgba(255,255,255,0.5)' }}
        >Slides</div>
        <div className="slide-area relative" ref={containerRef}>
          <div className="flex flex-wrap gap-2">
            {list.map((slide, i) => {
              const id = items[i];
              const color = getTileColor(slide, i);
              return (
                <SortableSlideCard
                  key={id}
                  id={id}
                  index={i}
                  color={color}
                  isActive={i === activeIndex}
                  isPendingDelete={i === pendingDeleteIndex}
                  canDelete={canDelete}
                  onSelect={onSelect}
                  onRequestDelete={handleRequestDelete}
                  onDuplicate={onDuplicate}
                />
              );
            })}

            <button
              onClick={onAdd}
              className="slide-card slide-card-inactive"
              title="Add slide"
              aria-label="Add slide"
              style={{ cursor: 'pointer' }}
            >
              <IconPlus className="h-3 w-3 slide-number" />
              <span className="slide-number">Add</span>
            </button>
          </div>

          {/* Inline delete confirmation popup */}
          {pendingDeleteIndex !== null && (
            <div
              className="absolute z-20 w-[240px] bg-[#1a1214] border border-red-500/15 rounded-lg px-2.5 py-2.5 shadow-lg shadow-black/40"
              style={{ top: popupPos.top, left: popupPos.left }}
            >
              <div className="flex items-start gap-2 text-[11px] text-red-400/80">
                <svg className="w-3.5 h-3.5 flex-shrink-0 mt-[1px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <span>
                  Remove <strong>Slide {pendingDeleteIndex + 1}</strong>?
                </span>
              </div>
              <div className="flex gap-2 mt-2.5 ml-5">
                <button
                  onClick={handleDeleteConfirm}
                  className="px-3 py-1 text-[11px] font-medium rounded-md bg-red-500/20 text-red-300 hover:bg-red-500/30 transition-colors"
                >
                  Remove
                </button>
                <button
                  onClick={handleDeleteCancel}
                  className="px-3 py-1 text-[11px] font-medium rounded-md bg-white/[0.04] text-white/50 hover:bg-white/[0.08] hover:text-white/70 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </SortableContext>
    </DndContext>
  );
}
